import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {Button, Paper, Popper} from "@mui/material";
import {Ref} from "react";

interface Props {
    /** Read fresh on every reposition, so the bubble follows the text as the page scrolls. */
    getAnchorRect: () => DOMRect
    /**
     * The text container the selection lives in. A virtual anchor has no scroll parents of its own,
     * so without this Popper never learns that the pane it hangs over is being scrolled.
     */
    contextElement?: Element | null
    /** The caller holds this so a press on the bubble does not count as "pressed elsewhere". */
    bubbleRef: Ref<HTMLDivElement>
    label: string
    onApply: () => void
    disabled?: boolean
}

/**
 * The confirm step: a selection does nothing on its own, this button applies it.
 *
 * Rendered through a Popper, which portals it to `document.body` — deliberately OUTSIDE the answer
 * container. Anything inside that container counts as answer text: its characters would land in
 * `Range.toString()` and shift every offset after it, so the button's own label would silently
 * corrupt the redaction it creates.
 *
 * The anchor is a virtual element rather than a DOM node, because what the bubble hangs off is not
 * an element at all — it is the last client rect of the selection. That is also why it hides itself
 * on `data-popper-reference-hidden` and not on `data-popper-escaped`: a bubble hanging past the
 * bottom of the pane is the normal case for a selection on its last line, while a bubble whose
 * SELECTION has scrolled away is pointing at nothing.
 */
const SurveyBlackoutConfirmBubble = ({
    getAnchorRect,
    contextElement,
    bubbleRef,
    label,
    onApply,
    disabled
}: Props) => (
    <Popper
        open
        anchorEl={{getBoundingClientRect: getAnchorRect, contextElement: contextElement ?? undefined}}
        placement={'bottom-end'}
        modifiers={[{name: 'offset', options: {offset: [0, 6]}}]}
        sx={theme => ({
            zIndex: theme.zIndex.tooltip,
            // Popper's `hide` modifier already marks the moment the selection scrolls out of the
            // pane it lives in; without this it only marks it, and the bubble stays on screen —
            // clamped into view, offering to black out text nobody can see any more. Hidden rather
            // than unmounted, so Popper keeps positioning it and it simply comes back on scroll;
            // `visibility` alone still leaves it clickable, hence the pointer-events.
            '&[data-popper-reference-hidden]': {
                visibility: 'hidden',
                pointerEvents: 'none',
            },
        })}
    >
        <Paper
            ref={bubbleRef}
            elevation={8}
            // Pressing the button must not take the selection down with it: the browser collapses a
            // selection on mousedown outside it, and while the applied range is read from state
            // either way, the highlight staying put while the user reaches for the button is the
            // entire point of a confirm step.
            onMouseDown={event => event.preventDefault()}
            sx={{borderRadius: 5, overflow: 'hidden', userSelect: 'none'}}
        >
            <Button
                size={'small'}
                onClick={onApply}
                disabled={disabled}
                startIcon={<VisibilityOffOutlinedIcon fontSize={'small'}/>}
                sx={{px: 1.5, textTransform: 'none', whiteSpace: 'nowrap'}}
            >
                {label}
            </Button>
        </Paper>
    </Popper>
);

export default SurveyBlackoutConfirmBubble;
